import { Modal, ModalProps } from "antd"
import { useTranslation } from "react-i18next"
import { object, string } from "yup"
import { Controller, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { FormControl } from "@atoms"
import TextArea from "antd/es/input/TextArea"
import { useToggleAbsence } from "./queries"
import { useMemberId } from "pages/member"

interface Props extends ModalProps {
  data?: string
  onClose: () => void
}

const PlannedAbsenceModal: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const memberId = useMemberId()
  const form = usePlannedAbsenceForm()
  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = form

  const onClose = () => {
    props.onClose()
    reset()
  }

  const plannedAbsence = useToggleAbsence(memberId!, {
    onSuccess() {
      onClose()
    },
  })

  const onSubmit = (data: any) => {
    plannedAbsence.mutate({ ...data, eventId: props.data })
  }

  return (
    <Modal
      title={t("events.motivated_absence")}
      onCancel={onClose}
      okText={t("global.save")}
      cancelText={t("global.cancel")}
      onOk={handleSubmit(onSubmit)}
      confirmLoading={plannedAbsence.isLoading}
      {...props}
    >
      <form>
        <FormControl>
          <Controller
            name="reason"
            control={control}
            render={({ field }) => (
              <TextArea
                placeholder={t("events.comment_for_motivated_absence")}
                rows={4}
                status={errors.reason && "error"}
                {...field}
              />
            )}
          />

          {errors.reason && (
            <FormControl.HelperText state="error">
              {errors.reason.message}
            </FormControl.HelperText>
          )}
        </FormControl>
      </form>
    </Modal>
  )
}

export default PlannedAbsenceModal

interface IPlannedAbsenceForm {
  reason: string
}

export function usePlannedAbsenceForm(
  defaultValues?: Partial<IPlannedAbsenceForm>
) {
  const { t } = useTranslation()
  const schema = object({
    reason: string().required(
      t("global.requiredField", {
        field: t("events.comment_for_motivated_absence"),
      })
    ),
  }).required()

  return useForm<IPlannedAbsenceForm>({
    resolver: yupResolver(schema),
    defaultValues,
  })
}
